import { useState, type ReactNode } from "react";
import {
  Box,
  Flex,
  Text,
  TextField,
  IconButton,
  Select,
  Checkbox,
} from "@radix-ui/themes";
import {
  MagnifyingGlassIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons";
import { useManagerContext } from "./context";
import type { TextSearchProps, BinarySearchProps } from "./types";

type SearchProps = {
  children: ReactNode;
};

export function Search({ children }: SearchProps) {
  const { toggleSearch } = useManagerContext();

  return (
    <Box
      style={{
        width: "280px",
        flexShrink: 0,
        borderLeft: "1px solid var(--gray-a6)",
        paddingLeft: "var(--space-4)",
      }}
    >
      <Flex justify="between" align="center" mb="3">
        <Text size="3" weight="bold">
          Search
        </Text>
        <IconButton variant="ghost" size="1" onClick={toggleSearch}>
          <Cross2Icon />
        </IconButton>
      </Flex>

      <Flex direction="column" gap="3">
        {children}
      </Flex>
    </Box>
  );
}

Search.displayName = "ManagerSearch";

export function TextSearch({ columns, placeholder }: TextSearchProps) {
  const { searchParams, setSearchText } = useManagerContext();

  // Store columns in a ref so they can be accessed by fetchData
  // This is a no-op for rendering, columns are used by the page's fetchData wrapper
  void columns;

  return (
    <Box>
      <Text as="label" size="2" mb="1" weight="medium">
        Text Search
      </Text>
      <TextField.Root
        size="2"
        placeholder={placeholder ?? "Search..."}
        value={searchParams.text ?? ""}
        onChange={(e) => setSearchText(e.target.value)}
      >
        <TextField.Slot>
          <MagnifyingGlassIcon height="16" width="16" />
        </TextField.Slot>
        {searchParams.text && (
          <TextField.Slot>
            <IconButton
              size="1"
              variant="ghost"
              onClick={() => setSearchText("")}
            >
              <Cross2Icon height="14" width="14" />
            </IconButton>
          </TextField.Slot>
        )}
      </TextField.Root>
    </Box>
  );
}

export function BinarySearch({
  filterKey,
  label,
  yesLabel = "Yes",
  noLabel = "No",
  allLabel = "All",
}: BinarySearchProps) {
  const { searchParams, setBinarySearch } = useManagerContext();

  return (
    <Box>
      <Text as="div" size="2" mb="1" weight="medium">
        {label}
      </Text>
      <Select.Root
        value={searchParams.binary?.[filterKey] ?? "all"}
        onValueChange={(value) =>
          setBinarySearch(
            filterKey,
            value === "all" ? undefined : (value as "yes" | "no")
          )
        }
      >
        <Select.Trigger style={{ width: "100%" }} />
        <Select.Content>
          <Select.Item value="all">{allLabel}</Select.Item>
          <Select.Item value="yes">{yesLabel}</Select.Item>
          <Select.Item value="no">{noLabel}</Select.Item>
        </Select.Content>
      </Select.Root>
    </Box>
  );
}

export function SelectSearch<T>({
  filterKey,
  label,
  options,
  getOptionLabel,
  getOptionValue,
  allLabel = "All",
  placeholder,
}: {
  filterKey: string;
  label: string;
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  allLabel?: string;
  placeholder?: string;
}) {
  const { searchParams, setFilter } = useManagerContext();
  const currentValue = searchParams.filters?.[filterKey];

  return (
    <Box>
      <Text as="div" size="2" mb="1" weight="medium">
        {label}
      </Text>
      <Select.Root
        value={currentValue ? String(currentValue) : "all"}
        onValueChange={(value) =>
          setFilter(filterKey, value === "all" ? undefined : value)
        }
      >
        <Select.Trigger
          style={{ width: "100%" }}
          placeholder={placeholder ?? `Select ${label.toLowerCase()}...`}
        />
        <Select.Content>
          <Select.Item value="all">{allLabel}</Select.Item>
          {options.map((option) => {
            const value = String(getOptionValue(option));
            return (
              <Select.Item key={value} value={value}>
                {getOptionLabel(option)}
              </Select.Item>
            );
          })}
        </Select.Content>
      </Select.Root>
    </Box>
  );
}

export function MultiSelectSearch<T>({
  filterKey,
  label,
  options,
  getOptionLabel,
  getOptionValue,
  renderOption,
  defaultExpanded = false,
}: {
  filterKey: string;
  label: string;
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  renderOption?: (option: T, label: string) => ReactNode;
  defaultExpanded?: boolean;
}) {
  const { searchParams, setFilter } = useManagerContext();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const currentValues =
    (searchParams.filters?.[filterKey] as (string | number)[] | undefined) ??
    [];

  const handleToggle = (value: string | number) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    setFilter(filterKey, newValues.length > 0 ? newValues : undefined);
  };

  const selectedCount = currentValues.length;

  return (
    <Box>
      <Flex
        justify="between"
        align="center"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Text as="div" size="2" weight="medium">
          {label}
          {selectedCount > 0 && (
            <Text size="1" color="gray" ml="1">
              ({selectedCount})
            </Text>
          )}
        </Text>
        <IconButton
          variant="ghost"
          size="1"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
      </Flex>
      {expanded && (
        <Flex direction="column" gap="1" mt="2">
          {options.map((option) => {
            const value = getOptionValue(option);
            const isSelected = currentValues.includes(value);
            const labelText = getOptionLabel(option);
            return (
              <Text
                key={String(value)}
                as="label"
                size="2"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="1"
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(value)}
                />
                {renderOption ? renderOption(option, labelText) : labelText}
              </Text>
            );
          })}
        </Flex>
      )}
    </Box>
  );
}
